import { useState } from "react";
import CustomPopUp from "../global/CustomPopup";
import Button from "../organism/Button";
import ChannelSelector from "./ChannelSelector";
import DeliveryModeSection from "./DeliveryModeSection";
import OrganizationsPreferencesList, {
  OrganizationPreferenceRow,
} from "./OrganizationsPreferencesList";
import {
  CommunicationChannel,
  DeliveryMode,
  SummaryFrequency,
  WeekDay,
} from "../../constants/communicationConstants";

const STEPS = [
  { id: "intro", title: "How it works" },
  { id: "channels", title: "Channels" },
  { id: "delivery", title: "Delivery" },
  { id: "organizations", title: "Organizations" },
] as const;

interface CommunicationSetupModalProps {
  channels: CommunicationChannel[];
  deliveryMode: DeliveryMode;
  summaryTime: string;
  summaryFrequency: SummaryFrequency;
  summaryDayOfWeek: WeekDay;
  timezone: string;
  organizations: OrganizationPreferenceRow[];
  email?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
  mobilePhoneVerified?: boolean;
  isSaving?: boolean;
  onChannelsChange: (channels: CommunicationChannel[]) => void;
  onDeliveryModeChange: (mode: DeliveryMode) => void;
  onSummaryTimeChange: (time: string) => void;
  onSummaryFrequencyChange: (frequency: SummaryFrequency) => void;
  onSummaryDayOfWeekChange: (day: WeekDay) => void;
  onTimezoneChange: (timezone: string) => void;
  onToggleExternal: (orgId: string, enabled: boolean) => void;
  onCustomize: (orgId: string) => void;
  onSave: () => void;
  onSkip: () => void;
  onAddVerifyPhone?: () => void;
}

function CommunicationSetupModal({
  channels,
  deliveryMode,
  summaryTime,
  summaryFrequency,
  summaryDayOfWeek,
  timezone,
  organizations,
  email,
  mobilePhone,
  fax,
  mobilePhoneVerified,
  isSaving = false,
  onChannelsChange,
  onDeliveryModeChange,
  onSummaryTimeChange,
  onSummaryFrequencyChange,
  onSummaryDayOfWeekChange,
  onTimezoneChange,
  onToggleExternal,
  onCustomize,
  onSave,
  onSkip,
  onAddVerifyPhone,
}: Readonly<CommunicationSetupModalProps>): JSX.Element {
  const [stepIndex, setStepIndex] = useState(0);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;
  const canContinueChannels = channels.length > 0;

  function goNext() {
    if (STEPS[stepIndex].id === "channels" && !canContinueChannels) {
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  function goBack() {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  }

  return (
    <CustomPopUp
      popUpTitle="Unified communication preferences"
      popUpSubtitle={`Step ${stepIndex + 1} of ${STEPS.length}: ${STEPS[stepIndex].title}`}
      hidePopUp={onSkip}
      warningPopupNotificationSettings
      subTitleClassName="!my-1 text-textSecondaryGray"
    >
      <div className="max-h-[65vh] overflow-auto pr-1 min-w-[min(100%,560px)]">
        <div className="flex gap-1 mb-5">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`h-1.5 flex-1 rounded-full ${
                index <= stepIndex ? "bg-primary" : "bg-dropDownBorder"
              }`}
              aria-hidden
            />
          ))}
        </div>

        {STEPS[stepIndex].id === "intro" && (
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-lightBlue flex items-center justify-center flex-shrink-0">
                <svg
                  className="text-primary"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 0 0 2 2Zm6-6V11c0-3.07-1.64-5.64-4.5-6.32V4a1.5 1.5 0 0 0-3 0v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-royalBlue">
                  Stay informed, on your terms.
                </h3>
                <p className="text-sm text-textSecondaryGray mt-2">
                  Choose email, SMS or fax, then set one delivery schedule. Your
                  preferences apply across your organizations unless you
                  customize one.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-dropDownBorder">
              <div>
                <p className="text-xs font-semibold uppercase text-textSecondaryGray tracking-wide">
                  Channels
                </p>
                <p className="text-sm text-royalBlue mt-1">Email, SMS or fax</p>
              </div>
              <div className="md:border-l md:border-dropDownBorder md:pl-4">
                <p className="text-xs font-semibold uppercase text-textSecondaryGray tracking-wide">
                  Delivery
                </p>
                <p className="text-sm text-royalBlue mt-1">
                  Immediately or a scheduled summary
                </p>
              </div>
            </div>
            <p className="text-xs text-textSecondaryGray">
              You can change these anytime from My preferences. Your existing
              settings stay in place until you save.
            </p>
          </div>
        )}

        {STEPS[stepIndex].id === "channels" && (
          <div>
            <h3 className="font-semibold text-royalBlue mb-1">
              Where should we send notifications?
            </h3>
            <p className="text-sm text-textSecondaryGray mb-4">
              Select at least one channel. Verify your mobile number to enable
              SMS.
            </p>
            <ChannelSelector
              channels={channels}
              onChange={onChannelsChange}
              email={email}
              mobilePhone={mobilePhone}
              fax={fax}
              mobilePhoneVerified={mobilePhoneVerified}
              readOnly={isSaving}
              onAddVerifyPhone={onAddVerifyPhone}
            />
          </div>
        )}

        {STEPS[stepIndex].id === "delivery" && (
          <div>
            <h3 className="font-semibold text-royalBlue mb-1">
              When should we send them?
            </h3>
            <p className="text-sm text-textSecondaryGray mb-4">
              One delivery schedule for all notification types.
            </p>
            <DeliveryModeSection
              deliveryMode={deliveryMode}
              summaryTime={summaryTime}
              summaryFrequency={summaryFrequency}
              summaryDayOfWeek={summaryDayOfWeek}
              timezone={timezone}
              onDeliveryModeChange={onDeliveryModeChange}
              onSummaryTimeChange={onSummaryTimeChange}
              onSummaryFrequencyChange={onSummaryFrequencyChange}
              onSummaryDayOfWeekChange={onSummaryDayOfWeekChange}
              onTimezoneChange={onTimezoneChange}
              readOnly={isSaving}
            />
          </div>
        )}

        {STEPS[stepIndex].id === "organizations" && (
          <div>
            <h3 className="font-semibold text-royalBlue mb-1">
              Your organizations
            </h3>
            <p className="text-sm text-textSecondaryGray mb-4">
              Turn external notifications on or off per organization, or
              customize channels and delivery for one org.
            </p>
            <OrganizationsPreferencesList
              organizations={organizations}
              onToggleExternal={onToggleExternal}
              onCustomize={onCustomize}
              readOnly={isSaving}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-dropDownBorder">
        <button
          type="button"
          onClick={onSkip}
          disabled={isSaving}
          className="text-sm text-primary font-semibold disabled:opacity-50"
        >
          Skip for now
        </button>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button
              type="button"
              buttonTheme="secondary"
              disabled={isSaving}
              onClick={goBack}
              textInformation={{
                text: "Back",
                fontWeight: "font-semibold",
              }}
            />
          )}
          {isLast ? (
            <Button
              type="button"
              buttonTheme="primary"
              disabled={isSaving || !canContinueChannels}
              loading={isSaving}
              onClick={onSave}
              textInformation={{
                text: isSaving ? "Saving..." : "Save preferences",
                fontWeight: "font-semibold",
              }}
            />
          ) : (
            <Button
              type="button"
              buttonTheme="primary"
              disabled={
                isSaving ||
                (STEPS[stepIndex].id === "channels" && !canContinueChannels)
              }
              onClick={goNext}
              textInformation={{
                text: "Next →",
                fontWeight: "font-semibold",
              }}
            />
          )}
        </div>
      </div>
    </CustomPopUp>
  );
}

export default CommunicationSetupModal;
